import { WebStorageStateStore } from "oidc-client-ts";
import type { ReactNode } from "react";
import { AuthProvider as OidcProvider } from "react-oidc-context";

import type { AppConfig } from "../config";
import { memoryStore } from "./memoryStore";

/**
 * Cognito's hosted UI is a standards-compliant OIDC provider with a discovery
 * document, so a generic OIDC client is the right abstraction — no
 * Cognito-specific SDK. (aws-amplify was rejected: it defaults to localStorage
 * for tokens, which the docs forbid, and fighting a library's default on the
 * one property that matters most is the wrong shape.)
 */
export function AuthProvider({
  config,
  children,
}: {
  config: AppConfig;
  children: ReactNode;
}) {
  return (
    <OidcProvider
      authority={config.authority}
      client_id={config.clientId}
      redirect_uri={`${window.location.origin}/callback`}
      // The TRAILING SLASH is load-bearing. Cognito compares this against its
      // registered logout URLs as an exact string — AdminPlatformStack builds
      // them as `${origin}/`, so `${origin}` alone is rejected with "An error
      // was encountered with the requested page", naming nothing.
      post_logout_redirect_uri={`${window.location.origin}/`}
      // Authorization code + PKCE. The pool has implicit disabled, so this is
      // the only flow that works — stated here too because a future edit to
      // `response_type` would fail confusingly rather than obviously.
      response_type="code"
      scope="openid email profile"
      // TOKENS in memory, never web storage. This is the constraint from
      // docs/ARCHITECTURE.md, and it is what `userStore` governs.
      userStore={new WebStorageStateStore({ store: memoryStore })}
      // STATE in sessionStorage, and it CANNOT be in memory — an in-memory
      // stateStore is not stricter, it is broken.
      //
      // The authorization-code flow writes a `state` value, then does a FULL
      // PAGE REDIRECT to Cognito. That destroys the page and everything held in
      // memory. When Cognito redirects back with ?code&state, the library looks
      // up the value it wrote — finds nothing — and fails with "No matching
      // state found in storage". Sign-in becomes impossible.
      //
      // `state` is not a credential. It is a random CSRF nonce, valid for the
      // seconds between leaving and returning, and useless to anyone who steals
      // it: redeeming the code also requires the PKCE verifier and the code
      // itself. sessionStorage scopes it to this tab and clears it on close.
      //
      // The security property the docs care about — no TOKEN in web storage —
      // is unaffected, and asserted by a test.
      stateStore={new WebStorageStateStore({ store: window.sessionStorage })}
      // Silent renew uses a hidden iframe, which browsers blocking
      // third-party cookies defeat — Cognito's session cookie is third-party
      // relative to this origin. Left off rather than shipped as something
      // that works locally and fails for real users; App.tsx handles the
      // reload case with an explicit prompt=none attempt instead.
      automaticSilentRenew={false}
      onSigninCallback={() => {
        // Strip ?code= and ?state= so a refresh does not replay the callback.
        window.history.replaceState({}, document.title, window.location.pathname);
      }}
    >
      {children}
    </OidcProvider>
  );
}
